import { z } from 'zod';

const addressSchema = z.object({
  addressLine1: z.string().min(1, 'Address line 1 is required'),
  addressLine2: z.string().optional(),
  city: z.string().min(1, 'City is required'),
  state: z.string().optional(),
  zipCode: z.string().optional(),
  country: z.string().min(1, 'Country is required'),
  latitude: z.number().optional(),
  longitude: z.number().optional()
});
export const TimeSlotSchema = z
  .object({
    startTime: z.number().min(0, 'Start time must be >= 0').max(1440, 'Start time must be <= 1440'),
    endTime: z.number().min(0, 'End time must be >= 0').max(2880, 'End time must be <= 2880')
  })
  .refine((data) => data.endTime > data.startTime, {
    message: 'End time must be after start time',
    path: ['endTime']
  });

// Payment method schema
export const PaymentMethodSchema = z.enum(['cash', 'online']);
export type PaymentMethod = z.infer<typeof PaymentMethodSchema>;

/**
 * Full resolved location of a booked occurrence, mirroring the API's
 * `AvailabilitySlotLocation`. Stored on the form when a slot is selected so the
 * wizard reads the per-slot address/coords/radius instead of an activity-level one.
 */
export const SlotLocationSchema = z.object({
  kind: z.enum(['venue', 'at_home', 'online']),
  label: z.string().nullish(),
  addressLine1: z.string().nullish(),
  addressLine2: z.string().nullish(),
  city: z.string().nullish(),
  state: z.string().nullish(),
  zipCode: z.string().nullish(),
  country: z.string().nullish(),
  latitude: z.number().nullish(),
  longitude: z.number().nullish(),
  radius: z.number().nullish(),
  /** The Hub this venue is at, as the availability response names it. */
  hub: z.object({ id: z.string(), name: z.string() }).nullish(),
  isAtHome: z.boolean(),
  timezone: z.string().nullish(),
  meetingProvider: z.enum(['toptoptime', 'byo', 'manual']).nullish(),
  meetingUrl: z.string().nullish()
});
export type SlotLocationData = z.infer<typeof SlotLocationSchema>;

const BaseBookingFormSchema = z.object({
  bookingType: z.enum(['SINGLE', 'GROUP', 'RANGE'], {
    required_error: 'Slot type is required',
    invalid_type_error: 'Invalid slot type'
  }),

  bookingDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be in YYYY-MM-DD format'),

  timeSlot: TimeSlotSchema,

  numberOfPeople: z
    .number()
    .min(1, 'Number of people must be at least 1')
    .max(50, 'Number of people cannot exceed 50')
    .optional(),

  address: addressSchema.optional(),

  notes: z.string().max(500, 'Notes cannot exceed 500 characters').optional(),

  slotId: z.string().optional(),
  activityId: z.string().optional(),
  basePrice: z.number().min(0).optional(),

  // Chosen delivery location for the booking.
  locationId: z.string().optional(),
  /** UI helper: the chosen location's canonical 3-value kind. The address step is
   *  gated on `slotLocation.kind === 'at_home'`; this mirrors that kind. */
  locationKind: z.enum(['venue', 'at_home', 'online']).optional(),

  /**
   * The selected occurrence's FULL resolved location (per-slot), as returned by
   * getAvailableSlots. Drives the per-slot address shown in the summary/review and
   * the at_home radius check against THIS slot's coords + radius. Absent for legacy
   * slots with no referenced location (the wizard falls back to the activity).
   */
  slotLocation: SlotLocationSchema.optional(),

  // Added for payment step - stores the booking ID after initial creation
  bookingId: z.string().optional(),

  // Payment method selection
  paymentMethod: PaymentMethodSchema.optional(),

  // Slot's accepted payment methods (from selected slot)
  selectedSlotPaymentMethods: z.array(PaymentMethodSchema).optional(),

  // Cash is closed for this client on the selected slot: their first session with the pro is paid online
  firstBookingOnlineRequired: z.boolean().optional(),

  // Address radius validation flag - set to true when address is outside professional's service radius
  addressOutOfRadius: z.boolean().optional()
});

export const BookingFormSchema = BaseBookingFormSchema;

export const BookingSubmissionSchema = BaseBookingFormSchema.extend({
  slotId: z.string().min(1, 'Slot ID is required'),
  activityId: z.string().min(1, 'Activity ID is required')
});

export type BookingFormData = z.infer<typeof BaseBookingFormSchema>;
export type BookingSubmissionData = z.infer<typeof BookingSubmissionSchema>;
